import { memo, useMemo } from 'react';
import { Button, HStack } from '@chakra-ui/react';
import { BrandTooltip } from './ui/tooltip';
import { MultiToggleGroup } from './ui/multi-toggle-group';

/** `classification.domain` from Riftcodex — Body, Calm, Chaos, Fury, Mind, Order. */

const ICON_PX = 22;
const FILTER_ICON_PX = 26;

function domainIconSrc(slug: string): string {
  const base = import.meta.env.BASE_URL;
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}domain-icons/${slug}.png`;
}

function DomainGlyph({ slug, label, size = ICON_PX }: { slug: string; label: string; size?: number }) {
  return (
    <BrandTooltip content={label}>
      <img
        src={domainIconSrc(slug)}
        alt=""
        width={size}
        height={size}
        loading="eager"
        decoding="async"
        draggable={false}
        style={{
          display: 'block',
          width: size,
          height: size,
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />
    </BrandTooltip>
  );
}

const DOMAIN_ROWS = [
  { value: 'Body', slug: 'body' },
  { value: 'Calm', slug: 'calm' },
  { value: 'Chaos', slug: 'chaos' },
  { value: 'Fury', slug: 'fury' },
  { value: 'Mind', slug: 'mind' },
  { value: 'Order', slug: 'order' },
] as const;

export interface DomainFilterProps {
  selected: string[];
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  /** Icon-only row for dense toolbars (e.g. deck gallery). */
  compact?: boolean;
  /** On mobile, spread the icon row across the full width with even gaps. */
  spread?: boolean;
  /** When set, only these domains are rendered. */
  displayDomains?: string[];
}

export const DomainFilter = memo(function DomainFilter({
  selected,
  onChange,
  label = 'Domain',
  disabled = false,
  compact = false,
  spread = false,
  displayDomains,
}: DomainFilterProps) {
  const visibleRows = useMemo(
    () => displayDomains
      ? DOMAIN_ROWS.filter((row) => displayDomains.includes(row.value))
      : DOMAIN_ROWS,
    [displayDomains],
  );
  const options = useMemo(
    () =>
      visibleRows.map(({ value, slug }) => ({
        value,
        label: value,
        icon: <DomainGlyph slug={slug} label={value} size={FILTER_ICON_PX} />,
      })),
    [visibleRows],
  );

  if (compact) {
    return (
      <HStack
        gap={spread ? { base: 0, md: 1 } : 1}
        w={spread ? { base: '100%', md: 'auto' } : undefined}
        justify={spread ? { base: 'space-between', md: 'flex-start' } : undefined}
        flexWrap="nowrap"
        flexShrink={0}
        role="group"
        aria-label={label}
      >
        {visibleRows.map(({ value, slug }) => {
          const isSelected = selected.includes(value);
          const iconSize = spread ? undefined : ICON_PX;
          return (
            <BrandTooltip key={value} content={value}>
              <Button
                type="button"
                variant="outline"
                bg={isSelected ? 'accentMuted' : 'bgElevated'}
                borderWidth="hairline"
                borderColor={isSelected ? 'accentMuted' : 'border'}
                size="sm"
                minW={spread ? { base: '44px', md: '36px' } : '36px'}
                w={spread ? { base: '44px', md: '36px' } : undefined}
                h={spread ? { base: '44px', md: '36px' } : '36px'}
                p={1}
                flexShrink={0}
                borderRadius="full"
                onClick={() => onChange(value)}
                disabled={disabled}
                aria-pressed={isSelected}
                aria-label={value}
                transition="background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease"
                _hover={disabled ? {} : {
                  bg: isSelected ? 'accentMuted' : 'surface',
                  borderColor: isSelected ? 'accentHover' : 'textMuted',
                }}
              >
                <img
                  src={domainIconSrc(slug)}
                  alt=""
                  width={iconSize ?? 28}
                  height={iconSize ?? 28}
                  loading="eager"
                  decoding="async"
                  draggable={false}
                  style={{
                    display: 'block',
                    width: iconSize ?? 28,
                    height: iconSize ?? 28,
                    maxWidth: iconSize ?? 28,
                    maxHeight: iconSize ?? 28,
                    objectFit: 'contain',
                    flexShrink: 0,
                    opacity: 1,
                    transition: 'opacity 0.12s ease',
                  }}
                />
              </Button>
            </BrandTooltip>
          );
        })}
      </HStack>
    );
  }

  return (
    <MultiToggleGroup
      label={label}
      options={options}
      selected={selected}
      onChange={onChange}
      disabled={disabled}
      wrap
    />
  );
});
